import cx from 'classnames';
import Button from 'components/button/Button';
import { Carousel } from 'components/carousel/Carousel';
import {
  FILTER_MATRIX,
  PhotoFilterTypes,
} from 'components/photo-sheet/photo-sheet-utils';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import {
  FILTER_ITEM_SELECTABLE_PER_PAGE,
  FILTER_ITEM_SELECTABLE_PER_PAGE_EVENT,
  VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { isHigher } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { resourceUrl } from 'helpers/url.helper';
import { join, map, range, size, slice } from 'lodash';
import { FilterPhotoModel } from 'models/photo/filter.model';
import React, { useMemo } from 'react';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { SelfeAppStateModel } from '../../../models/app.model';
import EditPhotoSheetPreview from '../edit-photo/EditPhotoSheetPreview';
import './add-filter.css';

function AddFilter() {
  const dispatch = useAppDispatch();
  const { audio } = useSoundContext();
  const { listFilterPhotos, currentLayout } = useBoothAppContext();
  const { filterId, filterType, quantitySheet } = useAppSelector(
    (state: { selfeApp: SelfeAppStateModel }) => state.selfeApp,
  );
  const { isVerticalScreen, isEventBooth } = useAppSelector((s) => s.booth);

  const handleChangeAmountMorePic = (quantity: number) => () =>
    dispatch(setSelfeAppStateAction({ quantitySheet: quantity }));

  const handleSelectFilter =
    (id: FilterPhotoModel['id'], type: FilterPhotoModel['type']) => () => {
      if (filterId !== id) {
        audio?.playClickToSelect?.();
        dispatch(setSelfeAppStateAction({ filterId: id, filterType: type }));
      }
    };

  React.useEffect(() => {
    dispatch(
      setSelfeAppStateAction({
        filterId: filterId || listFilterPhotos?.data?.[0]?.id,
        filterType: filterType || listFilterPhotos?.data?.[0]?.type,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, listFilterPhotos]);

  const renderFilterItem = (filterData: FilterPhotoModel) => (
    <button
      type="button"
      className={cx('filter-item', {
        active: !!isEqualVal(filterId, filterData?.id),
      })}
      onClick={handleSelectFilter(filterData?.id, filterData?.type)}
    >
      <img
        src={resourceUrl(filterData?.image)}
        alt="filter-img"
        className="filter-thumbnail no-drag"
      />
      <svg className="thumbnail-filter-svg">
        <filter
          id={`filter-image-${filterData?.id}`}
          colorInterpolationFilters="sRGB"
        >
          <feColorMatrix
            type="matrix"
            values={join(
              size(filterData?.colorMatrix)
                ? filterData?.colorMatrix
                : FILTER_MATRIX?.[PhotoFilterTypes.NONE],
            )}
          />
        </filter>
        <image
          filter={`url(#filter-image-${filterData?.id})`}
          xlinkHref={resourceUrl(filterData?.image)}
          x="0"
          y="0"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
      <Typography
        className="filter-name line-clamp-1"
        variant={TYPOGRAPHY_VARIANTS.BODY}
      >
        {filterData?.name}
      </Typography>
    </button>
  );

  const isVerticalFrame = useMemo(
    () => isHigher(currentLayout?.height, currentLayout?.width),
    [currentLayout],
  );

  // Số filter item trên 1 page
  const filterPerPage = useMemo(() => {
    // Ưu tiên Event Booth
    if (isEventBooth) {
      return FILTER_ITEM_SELECTABLE_PER_PAGE_EVENT;
    }

    const verticalItemPerPage = isVerticalFrame
      ? VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE
      : FILTER_ITEM_SELECTABLE_PER_PAGE;

    return isVerticalScreen
      ? verticalItemPerPage
      : FILTER_ITEM_SELECTABLE_PER_PAGE;
  }, [isEventBooth, isVerticalScreen, isVerticalFrame]);

  const isFilterItemLowerFour = useMemo(
    () =>
      !isHigher(size(listFilterPhotos?.data), FILTER_ITEM_SELECTABLE_PER_PAGE),
    [listFilterPhotos?.data],
  );

  return (
    <>
      <div
        className={cx('add-filter edit-page', {
          'is-filter-lower-four': isFilterItemLowerFour,
        })}
      >
        <div className="edit-page__selection-section">
          <EditPhotoSheetPreview />
          <div
            className={`add-filter__grid--wrapper ${
              isEventBooth && 'event-filter-grid--wrapper'
            }`}
          >
            {size(listFilterPhotos?.data) > filterPerPage ? (
              <Carousel
                className="filter-page__filter-carousel"
                slides={map(
                  range(
                    1,
                    Math.ceil(
                      (listFilterPhotos?.totalRecord || 0) / filterPerPage,
                    ) + 1,
                  ),
                )}
                RenderSlide={(_, ind) => (
                  <div className="filter-group add-filter__grid">
                    {map(
                      slice(
                        listFilterPhotos?.data,
                        ind * filterPerPage,
                        (ind + 1) * filterPerPage,
                      ),
                      (item) => renderFilterItem(item),
                    )}
                  </div>
                )}
              />
            ) : (
              <div className="add-filter__grid">
                {map(listFilterPhotos?.data, (filterData: FilterPhotoModel) =>
                  renderFilterItem(filterData),
                )}
              </div>
            )}
            {isEventBooth && (
              <div className="w-full items-center justify-center pl-12 mb-20">
                <Typography className="my-4">Số lượng in</Typography>
                <div className="button-group-more-pic">
                  {map(currentLayout?.prices, (item) => (
                    <Button
                      key={item?.numberOfPicture}
                      className="pb-square-button"
                      variantFill={isEqualVal(
                        item?.numberOfPicture,
                        quantitySheet,
                      )}
                      onClick={handleChangeAmountMorePic(item?.numberOfPicture)}
                    >
                      {item?.numberOfPicture}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AddFilter;
